/**
 * FAQ page controller: live search, scroll-spy, and popular-link deep
 * navigation. Scoped entirely to the FAQ page — this file is only
 * requested by sections/faq-page-hero.liquid. Reuses the theme's shared
 * `debounce()` from global.js rather than redefining it.
 *
 * DOM is queried once on connect and cached; every keystroke only reads
 * the cached arrays and toggles the native `hidden` attribute, so a
 * search never re-queries the document.
 */
class FaqSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;

    this.rows = Array.from(document.querySelectorAll('.faq-page-row'));
    this.categories = Array.from(document.querySelectorAll('.faq-page-category'));
    this.noResults = document.getElementById('faq-page-no-results');
    this.navLinks = Array.from(document.querySelectorAll('.faq-page-layout__nav-link'));
    this.popularLinks = Array.from(document.querySelectorAll('.faq-page-popular-link'));

    const handler = typeof debounce === 'function'
      ? debounce(this.handleSearch.bind(this), 150)
      : this.handleSearch.bind(this);
    this.input.addEventListener('input', handler);

    this.popularLinks.forEach((link) => {
      link.addEventListener('click', this.handlePopularClick.bind(this));
    });

    this.setupScrollSpy();
    this.setupAccordionAnimations();

    if (window.location.hash) {
      this.openAndScroll(window.location.hash.slice(1), false);
    }
  }

  handleSearch() {
    const query = this.input.value.trim().toLowerCase();
    let anyVisible = false;

    this.categories.forEach((category) => {
      const rows = Array.from(category.querySelectorAll('.faq-page-row'));
      let categoryHasMatch = false;

      rows.forEach((row) => {
        const haystack = row.dataset.search || '';
        const matches = query === '' || haystack.indexOf(query) !== -1;

        row.hidden = !matches;

        if (!matches) {
          const details = row.querySelector('details');
          if (details) details.open = false;
        }

        if (matches) categoryHasMatch = true;
      });

      category.hidden = !categoryHasMatch;
      if (categoryHasMatch) anyVisible = true;
    });

    if (this.noResults) {
      this.noResults.hidden = query === '' || anyVisible;
    }
  }

  handlePopularClick(event) {
    const href = event.currentTarget.getAttribute('href') || '';
    if (href.charAt(0) !== '#') return;

    event.preventDefault();
    const targetId = href.slice(1);

    if (this.input.value !== '') {
      this.input.value = '';
      this.handleSearch();
    }

    this.openAndScroll(targetId, true);
  }

  openAndScroll(targetId, smooth) {
    const target = document.getElementById(targetId);
    if (!target) return;

    target.hidden = false;
    const category = target.closest('.faq-page-category');
    if (category) category.hidden = false;

    const details = target.tagName === 'DETAILS' ? target : target.querySelector('details');
    if (details) details.open = true;

    const summary = target.querySelector('summary');

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      if (summary) summary.focus({ preventScroll: true });
    });
  }

  setupScrollSpy() {
    if (!('IntersectionObserver' in window) || this.categories.length === 0 || this.navLinks.length === 0) {
      return;
    }

    const setActive = (anchorId) => {
      this.navLinks.forEach((link) => {
        link.classList.toggle('is-active', link.dataset.anchor === anchorId);
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.dataset.anchor);
          }
        });
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
    );

    this.categories.forEach((category) => observer.observe(category));
  }

  /**
   * Subtle 200-300ms height/opacity transition on manual open/close only
   * (native <details>/<summary> stays the real accessible trigger; a
   * click just gets intercepted to animate instead of snapping). Search
   * filtering and popular-link navigation still set `.open` directly for
   * an instant, unanimated state change, which is correct there.
   * Uses the Web Animations API — no library, ~30 lines.
   */
  setupAccordionAnimations() {
    if (typeof Element === 'undefined' || !Element.prototype.animate) return;

    document.querySelectorAll('.faq-page-row details').forEach((details) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.faq-page-row__answer');
      if (!summary || !content) return;

      summary.addEventListener('click', (event) => {
        if (details.dataset.animating === 'true') {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        if (details.open) {
          this.collapseRow(details, content);
        } else {
          this.expandRow(details, content);
        }
      });
    });
  }

  expandRow(details, content) {
    details.dataset.animating = 'true';
    details.open = true;
    const targetHeight = content.scrollHeight;

    const animation = content.animate(
      [
        { height: '0px', opacity: 0 },
        { height: `${targetHeight}px`, opacity: 1 },
      ],
      { duration: 250, easing: 'ease' }
    );

    animation.onfinish = () => {
      content.style.height = '';
      delete details.dataset.animating;
    };
  }

  collapseRow(details, content) {
    details.dataset.animating = 'true';
    const startHeight = content.scrollHeight;

    const animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 200, easing: 'ease' }
    );

    animation.onfinish = () => {
      details.open = false;
      content.style.height = '';
      delete details.dataset.animating;
    };
  }
}

if (!customElements.get('faq-search')) {
  customElements.define('faq-search', FaqSearch);
}
